'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useSSOStore } from '@/lib/sso/store';
import type { SSOProvider } from '@/lib/sso/types';
import { SAMLConfigForm } from './saml-config-form';
import { OIDCConfigForm } from './oidc-config-form';
import { DomainVerification } from './domain-verification';
import {
  Shield,
  Key,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Settings2,
} from 'lucide-react';

const PROVIDER_OPTIONS: { value: SSOProvider; label: string; description: string }[] = [
  { value: 'saml', label: 'SAML 2.0', description: 'Standard SAML identity provider' },
  { value: 'oidc', label: 'OpenID Connect', description: 'OAuth 2.0 based authentication' },
  { value: 'okta', label: 'Okta', description: 'Okta Workforce Identity' },
  { value: 'azure_ad', label: 'Azure AD', description: 'Microsoft Entra ID' },
  { value: 'google_workspace', label: 'Google Workspace', description: 'Google Cloud Identity' },
];

export function SSOConfigPage() {
  const {
    ssoConfig,
    ssoLoading,
    ssoError,
    loadSSOConfig,
    toggleSSO,
    enforceSSO,
    testSSOLogin,
    deleteSSOConfig,
    clearErrors,
  } = useSSOStore();

  const [selectedProvider, setSelectedProvider] = useState<SSOProvider | null>(null);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('config');

  useEffect(() => {
    loadSSOConfig();
    return () => clearErrors();
  }, [loadSSOConfig, clearErrors]);

  useEffect(() => {
    if (ssoConfig?.provider) {
      setSelectedProvider(ssoConfig.provider);
    }
  }, [ssoConfig]);

  const handleTest = async () => {
    setTesting(true);
    try {
      const redirectUrl = await testSSOLogin();
      window.open(redirectUrl, '_blank', 'noopener,noreferrer,width=600,height=700');
    } catch (error) {
      // Error handled by store
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete the SSO configuration? Users will need to use password authentication.')) {
      await deleteSSOConfig();
      setSelectedProvider(null);
    }
  };

  const getStatusBadge = () => {
    if (!ssoConfig) {
      return <Badge variant="secondary">Not Configured</Badge>;
    }
    if (!ssoConfig.is_enabled) {
      return <Badge variant="secondary">Disabled</Badge>;
    }
    if (ssoConfig.is_enforced) {
      return <Badge variant="default">Enforced</Badge>;
    }
    return <Badge variant="outline">Enabled</Badge>;
  };

  if (ssoLoading && !ssoConfig) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="size-6" />
            Single Sign-On (SSO)
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure SAML or OIDC authentication for your organization
          </p>
        </div>
        {getStatusBadge()}
      </div>

      {ssoError && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription>{ssoError}</AlertDescription>
        </Alert>
      )}

      {/* Provider Selection (when no config exists) */}
      {!ssoConfig && !selectedProvider && (
        <Card>
          <CardHeader>
            <CardTitle>Choose Identity Provider</CardTitle>
            <CardDescription>
              Select your identity provider to configure SSO
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {PROVIDER_OPTIONS.map((provider) => (
                <Card
                  key={provider.value}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setSelectedProvider(provider.value)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Key className="size-8 text-muted-foreground" />
                      <div>
                        <h3 className="font-medium">{provider.label}</h3>
                        <p className="text-sm text-muted-foreground">{provider.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuration */}
      {(ssoConfig || selectedProvider) && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="config">
              <Settings2 className="size-4 mr-2" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="domains">
              <CheckCircle2 className="size-4 mr-2" />
              Domain Verification
            </TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      {PROVIDER_OPTIONS.find(p => p.value === (ssoConfig?.provider || selectedProvider))?.label} Configuration
                    </CardTitle>
                    <CardDescription>
                      Configure your identity provider settings
                    </CardDescription>
                  </div>
                  {ssoConfig && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="sso-enabled"
                          checked={ssoConfig.is_enabled}
                          onCheckedChange={toggleSSO}
                          disabled={ssoLoading}
                        />
                        <Label htmlFor="sso-enabled">Enabled</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="sso-enforced"
                          checked={ssoConfig.is_enforced}
                          onCheckedChange={enforceSSO}
                          disabled={ssoLoading || !ssoConfig.is_enabled}
                        />
                        <Label htmlFor="sso-enforced">Enforce SSO</Label>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(selectedProvider === 'saml' || ssoConfig?.provider === 'saml') ? (
                  <SAMLConfigForm
                    config={ssoConfig}
                    onCancel={() => !ssoConfig && setSelectedProvider(null)}
                  />
                ) : (
                  <OIDCConfigForm
                    config={ssoConfig}
                    provider={selectedProvider || ssoConfig?.provider || 'oidc'}
                    onCancel={() => !ssoConfig && setSelectedProvider(null)}
                  />
                )}

                {ssoConfig && (
                  <div className="mt-6 pt-6 border-t flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={handleTest}
                      disabled={testing || !ssoConfig.is_enabled}
                    >
                      <ExternalLink className="size-4 mr-2" />
                      {testing ? 'Testing...' : 'Test SSO Login'}
                    </Button>
                    <Button variant="destructive" onClick={handleDelete}>
                      Delete Configuration
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="domains" className="mt-6">
            <DomainVerification
              domains={ssoConfig?.allowed_domains || []}
              verified={ssoConfig?.domain_verified || false}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* SSO Status Info */}
      {ssoConfig?.is_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">SSO Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Provider</p>
                <p className="font-medium">
                  {PROVIDER_OPTIONS.find(p => p.value === ssoConfig.provider)?.label}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Domain Verified</p>
                <p className="font-medium flex items-center gap-1">
                  {ssoConfig.domain_verified ? (
                    <><CheckCircle2 className="size-4 text-green-500" /> Yes</>
                  ) : (
                    <><XCircle className="size-4 text-red-500" /> No</>
                  )}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Auto Provision</p>
                <p className="font-medium">{ssoConfig.auto_provision_users ? 'Enabled' : 'Disabled'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Default Role</p>
                <p className="font-medium capitalize">{ssoConfig.default_role}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
